import React, { useEffect, useRef, useState } from "react";
import { X, Plus, Trash2 } from "lucide-react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import CustomSelect from "../../../components/ui/CustomSelect";
import UserSearchMultiSelect from "./UserSearchMultiSelect";
import { useCountries, useCurrencies } from "../../../services/queries/queries";
import { api } from "../../../services/api/axios";
import { useCampaign } from "../hooks/useCampaigns";

const TASK_TYPES = [
  { value: "GIFT_CARD_SELL_MIN_USD", label: "Gift Card Sell (min USD amount)" },
  { value: "SEND_MONEY_DISTINCT_COUNTRIES", label: "Send Money (N distinct countries)" },
  { value: "SEND_MONEY_TO_COUNTRY", label: "Send Money (to a specific country)" },
  { value: "CRYPTO_FUND_NETWORK", label: "Crypto Fund (specific currency/network)" },
  { value: "FUND_WALLET", label: "Fund Wallet (specific or any fiat currency)" },
  { value: "CURRENCY_CONVERT_PAIR", label: "Currency Convert (specific pair)" },
  { value: "CUSTOM_REQUEST", label: "Custom Request (e.g. social tag, review)" },
];

const VISIBILITY_OPTIONS = [
  { value: "ALL_USERS", label: "All Users" },
  { value: "SPECIFIC_USERS", label: "Specific Users" },
];

const STATUS_OPTIONS = [
  { value: "DRAFT", label: "Draft" },
  { value: "ACTIVE", label: "Active" },
  { value: "PAUSED", label: "Paused" },
  { value: "ENDED", label: "Ended" },
];

const DISPLAY_MODE_OPTIONS = [
  { value: "DISMISS_ONCE", label: "Dismiss once (default)" },
  { value: "ALWAYS_SHOW", label: "Always show" },
];

// The only currencies a reward can actually be paid out in - kept in sync
// with the wallet lookup CampaignsService.redeem() does on the backend.
const REWARD_CURRENCY_OPTIONS = [
  { value: "USD", label: "USD" },
  { value: "USDT", label: "USDT" },
  { value: "USDC", label: "USDC" },
];

// A curated, stable list rather than a live provider call - crypto networks
// change rarely and this is only used to narrow a campaign task, not to
// drive a real funding flow.
const CRYPTO_NETWORK_OPTIONS = [
  { value: "", label: "Any network" },
  { value: "TRON", label: "TRON" },
  { value: "ETHEREUM", label: "Ethereum" },
  { value: "BSC", label: "BSC" },
  { value: "POLYGON", label: "Polygon" },
  { value: "SOLANA", label: "Solana" },
  { value: "BASE", label: "Base" },
];

const emptyTask = () => ({ taskType: "GIFT_CARD_SELL_MIN_USD", config: {}, description: "" });

const toDateInputValue = (value) => (value ? new Date(value).toISOString().slice(0, 10) : "");

const buildInitialForm = (campaign) => ({
  name: campaign?.name || "",
  description: campaign?.description || "",
  rewardAmount: campaign?.rewardAmount ?? "",
  rewardCurrency: campaign?.rewardCurrency || "USD",
  status: campaign?.status || "DRAFT",
  startDate: toDateInputValue(campaign?.startDate),
  endDate: toDateInputValue(campaign?.endDate),
  requireKycVerified: campaign?.requireKycVerified ?? true,
  requiresApprovalToRedeem: campaign?.requiresApprovalToRedeem ?? false,
  minAccountAgeDays: campaign?.minAccountAgeDays ?? 0,
  maxAccountAgeDays: campaign?.maxAccountAgeDays ?? "",
  code: campaign?.code || "",
  maxRedemptions: campaign?.maxRedemptions ?? "",
  announcementTitle: campaign?.announcementTitle || "",
  announcementBodyHtml: campaign?.announcementBodyHtml || "",
  announcementAlertText: campaign?.announcementAlertText || "",
  announcementCtaLabel: campaign?.announcementCtaLabel || "",
  announcementDisplayMode: campaign?.announcementDisplayMode || "DISMISS_ONCE",
  visibility: campaign?.visibility || "ALL_USERS",
  hideFromRewardsList: campaign?.hideFromRewardsList ?? false,
  tasks: campaign?.tasks?.length
    ? campaign.tasks.map((t) => ({
        // Carried through to handleSubmit so the backend can tell an
        // edited existing task apart from a newly added one - without
        // this, editing was indistinguishable from replacing every task.
        uuid: t.uuid,
        taskType: t.taskType,
        config: t.config || {},
        description: t.description || "",
      }))
    : [emptyTask()],
});

const TaskConfigFields = ({ task, onChange, countryOptions, currencyOptions, cryptoCurrencyOptions, fiatCurrencyOptions, currenciesByCode }) => {
  const setConfig = (key, value) => onChange({ ...task, config: { ...task.config, [key]: value } });

  // The "to" side only offers currencies the DB actually says the selected
  // "from" currency can convert to - falls back to the full list until a
  // "from" is chosen, or if that currency has no canConvertTo data.
  const fromCurrency = currenciesByCode?.get(task.config.fromCurrency);
  const toCurrencyOptions =
    fromCurrency?.canConvertTo?.length
      ? fromCurrency.canConvertTo.map((code) => ({ value: code.toUpperCase(), label: code.toUpperCase() }))
      : currencyOptions;

  switch (task.taskType) {
    case "GIFT_CARD_SELL_MIN_USD":
      return (
        <input
          type="number"
          min="0"
          placeholder="Minimum USD amount, e.g. 100"
          value={task.config.minUsdAmount ?? ""}
          onChange={(e) => setConfig("minUsdAmount", Number(e.target.value))}
          className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
        />
      );
    case "SEND_MONEY_DISTINCT_COUNTRIES":
      return (
        <input
          type="number"
          min="1"
          placeholder="Minimum distinct countries, e.g. 2"
          value={task.config.minDistinctCountries ?? ""}
          onChange={(e) => setConfig("minDistinctCountries", Number(e.target.value))}
          className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
        />
      );
    case "SEND_MONEY_TO_COUNTRY":
      return (
        <div className="grid grid-cols-2 gap-2">
          <CustomSelect
            value={task.config.countryCode ?? ""}
            onChange={(v) => setConfig("countryCode", v)}
            options={countryOptions}
            placeholder="Destination country"
          />
          <CustomSelect
            value={task.config.currency ?? ""}
            onChange={(v) => setConfig("currency", v)}
            options={currencyOptions}
            placeholder="Currency sent"
          />
        </div>
      );
    case "CRYPTO_FUND_NETWORK":
      return (
        <div className="grid grid-cols-2 gap-2">
          <CustomSelect
            value={task.config.currency ?? ""}
            onChange={(v) => setConfig("currency", v)}
            options={cryptoCurrencyOptions}
            placeholder="Currency"
          />
          <CustomSelect
            value={task.config.network ?? ""}
            onChange={(v) => setConfig("network", v)}
            options={CRYPTO_NETWORK_OPTIONS}
            placeholder="Network (optional)"
          />
        </div>
      );
    case "FUND_WALLET":
      return (
        <CustomSelect
          value={task.config.currency ?? ""}
          onChange={(v) => setConfig("currency", v)}
          options={fiatCurrencyOptions}
          placeholder="Currency (optional)"
        />
      );
    case "CURRENCY_CONVERT_PAIR":
      return (
        <div className="grid grid-cols-2 gap-2">
          <CustomSelect
            value={task.config.fromCurrency ?? ""}
            onChange={(v) => setConfig("fromCurrency", v)}
            options={currencyOptions}
            placeholder="From currency"
          />
          <CustomSelect
            value={task.config.toCurrency ?? ""}
            onChange={(v) => setConfig("toCurrency", v)}
            options={toCurrencyOptions}
            placeholder="To currency"
          />
        </div>
      );
    case "CUSTOM_REQUEST":
      return (
        <input
          type="text"
          placeholder="Instructions shown to the user, e.g. Tag us on Instagram"
          value={task.config.instructions ?? ""}
          onChange={(e) => setConfig("instructions", e.target.value)}
          className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
        />
      );
    default:
      return null;
  }
};

const CampaignFormModal = ({ isOpen, onClose, onSubmit, isLoading, campaign }) => {
  // `campaign` here is the row from the LIST endpoint, which never includes
  // tasks (a separate table, only attached by the single-campaign endpoint).
  // Re-fetch the full campaign (with tasks) whenever editing, so the form
  // never opens with a blank task list even though tasks were actually saved.
  const isEditing = !!campaign?.uuid;
  const { data: campaignDetail } = useCampaign(
    isEditing && isOpen ? campaign.uuid : undefined
  );
  const fullCampaign = isEditing ? campaignDetail?.data : campaign;
  // Not derived from react-query's own isLoading/isFetching flags - those
  // can read as "not loading" for a render or two right after a query is
  // newly enabled, before the fetch has actually started, which let the
  // form initialize from an incomplete `fullCampaign` (i.e. no tasks yet)
  // and then never get corrected. Gating on the data itself has no such gap.
  const detailReady = !isEditing || !!fullCampaign;

  const [form, setForm] = useState(buildInitialForm(fullCampaign));
  const [targetUsers, setTargetUsers] = useState([]);
  const alertTextRef = useRef(null);

  const { data: countriesData } = useCountries();
  const countryOptions = (countriesData?.data || []).map((c) => ({
    value: c.countryCode,
    label: c.country,
  }));

  const { data: currenciesResponse } = useCurrencies();
  const currencies = currenciesResponse?.data || [];
  const currencyOptions = currencies.map((c) => ({
    value: c.currencyCode.toUpperCase(),
    label: c.currencyCode.toUpperCase(),
  }));
  const cryptoCurrencyOptions = [
    { value: "", label: "Any currency" },
    ...currencies
      .filter((c) => c.currencyClass === "global")
      .map((c) => ({ value: c.currencyCode.toUpperCase(), label: c.currencyCode.toUpperCase() })),
  ];
  const fiatCurrencyOptions = [
    { value: "", label: "Any currency" },
    ...currencies
      .filter((c) => c.currencyClass === "local")
      .map((c) => ({ value: c.currencyCode.toUpperCase(), label: c.currencyCode.toUpperCase() })),
  ];
  const currenciesByCode = new Map(currencies.map((c) => [c.currencyCode.toUpperCase(), c]));

  useEffect(() => {
    if (!isOpen || !detailReady) return;
    setForm(buildInitialForm(fullCampaign));
  }, [isOpen, fullCampaign, detailReady]);

  // The campaign only stores target uuids - resolve each to an email so the
  // picker can show who is actually targeted instead of raw uuids.
  useEffect(() => {
    if (!isOpen) return;
    const uuids = fullCampaign?.targetUserUuids || [];
    if (uuids.length === 0) {
      setTargetUsers([]);
      return;
    }
    let cancelled = false;
    Promise.all(
      uuids.map((uuid) =>
        api
          .get(`/v1/admin/personal/user/${uuid}`)
          .then((res) => ({ uuid, email: res.data?.data?.email || uuid }))
          .catch(() => ({ uuid, email: uuid })),
      ),
    ).then((users) => {
      if (!cancelled) setTargetUsers(users);
    });
    return () => {
      cancelled = true;
    };
  }, [isOpen, fullCampaign]);

  if (!isOpen) return null;

  const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleTaskChange = (index, updatedTask) => {
    setForm((prev) => ({
      ...prev,
      tasks: prev.tasks.map((t, i) => (i === index ? updatedTask : t)),
    }));
  };

  const addTask = () => setForm((prev) => ({ ...prev, tasks: [...prev.tasks, emptyTask()] }));

  const removeTask = (index) =>
    setForm((prev) => ({ ...prev, tasks: prev.tasks.filter((_, i) => i !== index) }));

  // Inserts the {{cta}} placement token at the current cursor position in
  // the Dismissible Alert Text field, so the admin controls exactly where
  // the CTA renders within the sentence rather than it always being fixed
  // at the start or end.
  const insertCtaToken = () => {
    const textarea = alertTextRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart ?? form.announcementAlertText.length;
    const end = textarea.selectionEnd ?? form.announcementAlertText.length;
    const next =
      form.announcementAlertText.slice(0, start) + "{{cta}}" + form.announcementAlertText.slice(end);
    handleChange("announcementAlertText", next);
    requestAnimationFrame(() => {
      textarea.focus();
      const cursor = start + "{{cta}}".length;
      textarea.setSelectionRange(cursor, cursor);
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.rewardAmount || form.tasks.length === 0) return;

    // `null`, not `undefined` - JSON.stringify silently drops undefined
    // keys, so an admin clearing one of these fields to blank would
    // otherwise never reach the server at all, and the old value would
    // stick around forever no matter how many times they "cleared" it.
    const payload = {
      name: form.name,
      description: form.description || null,
      rewardAmount: Number(form.rewardAmount),
      rewardCurrency: form.rewardCurrency,
      status: form.status,
      startDate: form.startDate || null,
      endDate: form.endDate || null,
      requireKycVerified: form.requireKycVerified,
      requiresApprovalToRedeem: form.requiresApprovalToRedeem,
      minAccountAgeDays: Number(form.minAccountAgeDays) || 0,
      maxAccountAgeDays: form.maxAccountAgeDays ? Number(form.maxAccountAgeDays) : null,
      code: form.code || null,
      maxRedemptions: form.maxRedemptions ? Number(form.maxRedemptions) : null,
      announcementTitle: form.announcementTitle || null,
      announcementBodyHtml: form.announcementBodyHtml || null,
      announcementAlertText: form.announcementAlertText || null,
      announcementCtaLabel: form.announcementCtaLabel || null,
      announcementDisplayMode: form.announcementDisplayMode,
      visibility: form.visibility,
      targetUserUuids:
        form.visibility === "SPECIFIC_USERS"
          ? targetUsers.map((u) => u.uuid)
          : null,
      hideFromRewardsList: form.hideFromRewardsList,
      tasks: form.tasks,
    };

    onSubmit(payload);
  };

  const isEdit = !!campaign;

  // Never render the real form (and never let it initialize state) until
  // the full campaign - tasks included - has actually loaded. Showing the
  // form early, even briefly, is exactly how "edit silently loses tasks"
  // happened: the form's own initial state has no way to tell "genuinely
  // has no tasks" apart from "hasn't loaded them yet."
  if (!detailReady) {
    return (
      <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50 transition-opacity">
        <div className="flex overflow-hidden justify-center items-center mx-4 w-full max-w-3xl bg-white rounded-2xl shadow-2xl h-[90vh]">
          <p className="text-sm text-gray-500">Loading campaign...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50 transition-opacity">
      <div className="overflow-hidden mx-4 w-full max-w-3xl bg-white rounded-2xl shadow-2xl h-[90vh] flex flex-col">
        <div className="flex justify-between items-center px-6 py-4 bg-gray-50 border-b">
          <h3 className="text-lg font-semibold text-gray-900">
            {isEdit ? "Edit Campaign" : "Create Campaign"}
          </h3>
          <button onClick={onClose} className="text-gray-400 transition-colors hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 px-6 py-5 space-y-6">
          {/* Core */}
          <section className="space-y-3">
            <h4 className="text-xs font-bold tracking-wide text-emerald-800 uppercase">Campaign</h4>
            <input
              type="text"
              placeholder="Campaign name"
              value={form.name}
              onChange={(e) => handleChange("name", e.target.value)}
              className="px-4 py-2.5 w-full text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
              required
            />
            <textarea
              placeholder="Description (optional, internal)"
              value={form.description}
              onChange={(e) => handleChange("description", e.target.value)}
              rows={2}
              className="px-4 py-2.5 w-full text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
            />
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
              <input
                type="number"
                min="0"
                placeholder="Reward amount"
                value={form.rewardAmount}
                onChange={(e) => handleChange("rewardAmount", e.target.value)}
                className="px-3 py-2.5 text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
                required
              />
              <CustomSelect
                value={form.rewardCurrency}
                onChange={(v) => handleChange("rewardCurrency", v)}
                options={REWARD_CURRENCY_OPTIONS}
                placeholder="Currency"
              />
              <CustomSelect
                value={form.status}
                onChange={(v) => handleChange("status", v)}
                options={STATUS_OPTIONS}
                placeholder="Status"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block mb-1 text-xs text-gray-500">Start Date</label>
                <input
                  type="date"
                  value={form.startDate}
                  onChange={(e) => handleChange("startDate", e.target.value)}
                  className="px-3 py-2.5 w-full text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="block mb-1 text-xs text-gray-500">End Date</label>
                <input
                  type="date"
                  value={form.endDate}
                  onChange={(e) => handleChange("endDate", e.target.value)}
                  className="px-3 py-2.5 w-full text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>
            <label className="flex gap-2 items-center text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.requiresApprovalToRedeem}
                onChange={(e) => handleChange("requiresApprovalToRedeem", e.target.checked)}
              />
              Require manual review before redemption
            </label>
            <p className="pl-5 -mt-1 text-xs text-gray-400">
              Once a user finishes every task, their redemption submits as a request instead of
              paying out immediately - you'll approve or reject it from this campaign's Redemptions tab.
            </p>
          </section>

          {/* Tasks */}
          <section className="space-y-3">
            <div className="flex justify-between items-center">
              <h4 className="text-xs font-bold tracking-wide text-emerald-800 uppercase">
                Tasks (all must complete)
              </h4>
              <button
                type="button"
                onClick={addTask}
                className="flex gap-1 items-center text-xs font-semibold text-emerald-700 hover:text-emerald-900"
              >
                <Plus size={14} /> Add task
              </button>
            </div>
            {form.tasks.map((task, index) => (
              <div key={index} className="p-3 space-y-2 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex gap-2 items-start">
                  <div className="flex-1">
                    <CustomSelect
                      value={task.taskType}
                      onChange={(v) => handleTaskChange(index, { ...task, taskType: v, config: {} })}
                      options={TASK_TYPES}
                      placeholder="Task type"
                    />
                  </div>
                  {form.tasks.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeTask(index)}
                      className="p-2 text-red-500 hover:text-red-700"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
                <TaskConfigFields
                  task={task}
                  onChange={(updated) => handleTaskChange(index, updated)}
                  countryOptions={countryOptions}
                  currencyOptions={currencyOptions}
                  cryptoCurrencyOptions={cryptoCurrencyOptions}
                  fiatCurrencyOptions={fiatCurrencyOptions}
                  currenciesByCode={currenciesByCode}
                />
                <input
                  type="text"
                  placeholder="User-facing task description, e.g. Sell $100 in gift cards"
                  value={task.description}
                  onChange={(e) => handleTaskChange(index, { ...task, description: e.target.value })}
                  className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            ))}
          </section>

          {/* Code */}
          <section className="space-y-3">
            <h4 className="text-xs font-bold tracking-wide text-emerald-800 uppercase">
              Redemption Code
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <input
                type="text"
                placeholder="Coupon code, e.g. SELL100GET10"
                value={form.code}
                onChange={(e) => handleChange("code", e.target.value.toUpperCase())}
                className="px-3 py-2.5 text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
              />
              <input
                type="number"
                min="1"
                placeholder="Max redemptions (optional - blank = unlimited)"
                value={form.maxRedemptions}
                onChange={(e) => handleChange("maxRedemptions", e.target.value)}
                className="px-3 py-2.5 text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <p className="text-xs text-gray-400">
              Who can redeem this code is controlled by Visibility below, and when it stops
              working is controlled by the campaign's End Date above - there's no separate
              expiry for the code itself.
            </p>
          </section>

          {/* Eligibility */}
          <section className="space-y-3">
            <h4 className="text-xs font-bold tracking-wide text-emerald-800 uppercase">Eligibility</h4>
            <div className="grid grid-cols-2 gap-3">
              <input
                type="number"
                min="0"
                placeholder="Account age: more than (days)"
                value={form.minAccountAgeDays}
                onChange={(e) => handleChange("minAccountAgeDays", e.target.value)}
                className="px-3 py-2.5 text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
              />
              <input
                type="number"
                min="0"
                placeholder="Account age: less than (days, optional)"
                value={form.maxAccountAgeDays}
                onChange={(e) => handleChange("maxAccountAgeDays", e.target.value)}
                className="px-3 py-2.5 text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <p className="-mt-1 text-xs text-gray-400">
              Account age targets who can see and redeem this campaign - e.g. "more than" 0 with "less
              than" 7 targets brand-new signups; "more than" 90 with no "less than" targets dormant
              accounts. Leave both blank for no age restriction.
            </p>
            <label className="flex gap-2 items-center text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.requireKycVerified}
                onChange={(e) => handleChange("requireKycVerified", e.target.checked)}
              />
              Require KYC verification to redeem
            </label>
          </section>

          {/* Visibility */}
          <section className="space-y-3">
            <h4 className="text-xs font-bold tracking-wide text-emerald-800 uppercase">Visibility</h4>
            <p className="text-xs text-gray-400">
              This is the only place who can see and redeem this campaign is set - each user can
              still only redeem it once, no matter who it's shown to.
            </p>
            <CustomSelect
              value={form.visibility}
              onChange={(v) => handleChange("visibility", v)}
              options={VISIBILITY_OPTIONS}
              placeholder="Visibility"
            />
            {form.visibility === "SPECIFIC_USERS" && (
              <UserSearchMultiSelect selected={targetUsers} onChange={setTargetUsers} />
            )}
            <label className="flex gap-2 items-start pt-1 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={form.hideFromRewardsList}
                onChange={(e) => handleChange("hideFromRewardsList", e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded text-emerald-600 focus:ring-emerald-500"
              />
              <span>
                Hide from consumer Rewards list (code-only distribution)
                <span className="block text-xs text-gray-400">
                  Also hides the announcement banner. Doesn't change who can redeem the
                  code - that's still controlled by Visibility above.
                </span>
              </span>
            </label>
          </section>

          {/* Announcement */}
          <section className="space-y-3">
            <h4 className="text-xs font-bold tracking-wide text-emerald-800 uppercase">
              Consumer Announcement
            </h4>
            <input
              type="text"
              placeholder="Pop Up Subject"
              value={form.announcementTitle}
              onChange={(e) => handleChange("announcementTitle", e.target.value)}
              className="px-3 py-2.5 w-full text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
            />
            <div>
              <label className="block mb-1 text-xs text-gray-500">Pop Up Body</label>
              <ReactQuill
                theme="snow"
                value={form.announcementBodyHtml}
                onChange={(html) => handleChange("announcementBodyHtml", html)}
              />
            </div>
            <div>
              <div className="flex justify-between items-center mb-1">
                <label className="text-xs text-gray-500">Dismissible Alert Text</label>
                <button
                  type="button"
                  onClick={insertCtaToken}
                  className="text-xs font-semibold text-emerald-700 hover:text-emerald-900"
                >
                  Insert CTA here
                </button>
              </div>
              <textarea
                ref={alertTextRef}
                placeholder="e.g. Complete tasks to earn $10 - {{cta}} to see how."
                value={form.announcementAlertText}
                onChange={(e) => handleChange("announcementAlertText", e.target.value)}
                rows={2}
                className="px-3 py-2.5 w-full font-mono text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
              />
              <p className="mt-1 text-xs text-gray-400">
                Place your cursor where you want the CTA button to appear, then click "Insert CTA here" -
                the {"{{cta}}"} token marks that spot. If omitted, the CTA renders at the end.
              </p>
            </div>
            <input
              type="text"
              placeholder="CTA label, e.g. Learn more"
              value={form.announcementCtaLabel}
              onChange={(e) => handleChange("announcementCtaLabel", e.target.value)}
              className="px-3 py-2.5 w-full text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
            />
            <div>
              <CustomSelect
                value={form.announcementDisplayMode}
                onChange={(v) => handleChange("announcementDisplayMode", v)}
                options={DISPLAY_MODE_OPTIONS}
                placeholder="When dismissed"
              />
              <p className="mt-1 text-xs text-gray-400">
                "Dismiss once" hides the banner for good once a user closes it. "Always show" brings
                it back on every load regardless of dismissal.
              </p>
            </div>
          </section>
        </form>

        <div className="flex gap-3 justify-end px-6 py-4 bg-gray-50 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 text-sm font-bold text-gray-700 bg-white rounded-xl border-2 border-gray-200 transition-all hover:bg-gray-50 active:scale-95"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isLoading}
            className={`px-5 py-2.5 text-sm font-bold text-white bg-emerald-600 rounded-xl transition-all active:scale-95 ${
              isLoading ? "opacity-50 cursor-not-allowed" : "hover:bg-emerald-700"
            }`}
          >
            {isLoading ? "Saving..." : isEdit ? "Save Changes" : "Create Campaign"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CampaignFormModal;
